import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import marketingService from '../../../services/api/marketingService';

const SIGNAL_META = {
  third_party_match: { label: 'Third-party match', icon: '⚠️', color: '#dc2626', bg: '#fef2f2' },
  our_filing_visible: { label: 'Filing visible', icon: '✅', color: '#047857', bg: '#ecfdf5' },
  not_found: { label: 'Not on register', icon: '—', color: '#64748b', bg: '#f1f5f9' },
};

function timeAgo(iso) {
  if (!iso) return '';
  const days = Math.floor((Date.now() - new Date(iso).getTime()) / 86400000);
  if (days < 1) return 'today';
  if (days === 1) return 'yesterday';
  return `${days}d ago`;
}

function TmWatch() {
  const navigate = useNavigate();
  const { data, isLoading, error, refetch, isFetching } = useQuery({
    queryKey: ['marketing', 'tmwatch'],
    queryFn: () => marketingService.getTmWatch(),
    staleTime: 1000 * 60,
  });

  if (isLoading) {
    return (
      <div className="mkt-loading">
        <div className="mkt-loading-spinner" />
        <p>Loading TM Watch...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="mkt-funnel-error">
        <p>Failed to load TM Watch: {error.message}</p>
        <button onClick={() => refetch()} className="mkt-btn mkt-btn-primary">Retry</button>
      </div>
    );
  }

  const { configured, apiError, checkedThisLoad, entries = [], summary = {} } = data || {};

  return (
    <div className="mkt-feedback">
      <div className="mkt-funnel-header">
        <h2>TM Watch</h2>
        <button className="mkt-btn mkt-btn-secondary" onClick={() => refetch()} disabled={isFetching}>
          {isFetching ? 'Checking…' : '🔄 Check more'}
        </button>
      </div>

      {!configured && (
        <div className="mkt-tmwatch-setup">
          <strong>IP Australia isn&apos;t connected yet.</strong> Register at
          {' '}portal.api.ipaustralia.gov.au, subscribe to the Trade Mark Search API, then add
          {' '}<code>IPA_CLIENT_ID</code> and <code>IPA_CLIENT_SECRET</code> to the API containers.
          The register checks start automatically once the keys are in.
        </div>
      )}
      {configured && apiError && (
        <div className="mkt-tmwatch-setup" style={{ borderColor: '#fecaca', background: '#fef2f2' }}>
          <strong>Register check failed:</strong> {apiError}
          <div style={{ fontSize: '12px', marginTop: '4px', color: '#64748b' }}>
            If this mentions a 404 or unexpected shape, the API paths may need calibrating
            (IPA_TOKEN_URL / IPA_SEARCH_URL env overrides).
          </div>
        </div>
      )}

      <div className="mkt-funnel-summary">
        <div className="mkt-summary-card">
          <span className="mkt-summary-label">Watched marks</span>
          <span className="mkt-summary-value">{summary.watched || 0}</span>
          <span className="mkt-summary-rate">{summary.checked || 0} checked · {summary.unchecked || 0} pending</span>
        </div>
        <div className="mkt-summary-card">
          <span className="mkt-summary-label">⚠️ Third-party matches</span>
          <span className="mkt-summary-value" style={{ color: '#dc2626' }}>{summary.thirdPartyMatches || 0}</span>
          <span className="mkt-summary-rate">someone else holds their name</span>
        </div>
        <div className="mkt-summary-card">
          <span className="mkt-summary-label">✅ Our filings visible</span>
          <span className="mkt-summary-value" style={{ color: '#047857' }}>{summary.ourFilingsVisible || 0}</span>
        </div>
        <div className="mkt-summary-card">
          <span className="mkt-summary-label">Not on register</span>
          <span className="mkt-summary-value">{summary.notFound || 0}</span>
          <span className="mkt-summary-rate">names still free</span>
        </div>
      </div>

      {configured && checkedThisLoad > 0 && (
        <p className="mkt-feedback-note">Checked {checkedThisLoad} marks against the register this load; marks are rechecked weekly.</p>
      )}

      <div className="mkt-feedback-list">
        {entries.map((e) => {
          const meta = e.tmWatch ? SIGNAL_META[e.tmWatch.signal] : null;
          const top = e.tmWatch?.exactMatches?.[0] || null;
          return (
            <div
              key={e.visitorId}
              className="mkt-feedback-row"
              onClick={() => navigate(`/marketing/visitor/${e.visitorId}`)}
            >
              <div className="mkt-feedback-row-main">
                <div className="mkt-feedback-row-top">
                  {meta ? (
                    <span className="mkt-feedback-reason" style={{ background: meta.bg, color: meta.color }}>
                      {meta.icon} {meta.label}
                    </span>
                  ) : (
                    <span className="mkt-feedback-reason" style={{ background: '#f1f5f9', color: '#94a3b8' }}>
                      ⏳ Not checked yet
                    </span>
                  )}
                  <span className="mkt-visitor-tm">&ldquo;{e.trademarkName}&rdquo;</span>
                  <span className="mkt-feedback-who">{e.name || e.email || 'Unknown'}</span>
                  {e.paid && <span className="mkt-badge mkt-badge-feedback-given">Customer</span>}
                </div>
                <div className="mkt-feedback-row-meta">
                  {top && (
                    <span>
                      {top.number ? `#${top.number}` : ''} {top.status ? `· ${top.status}` : ''}
                      {top.ownerName ? ` · owner: ${top.ownerName}` : ''}
                      {top.filedDate ? ` · filed ${top.filedDate}` : ''}
                    </span>
                  )}
                  {e.tmWatch && !top && e.tmWatch.matchCount > 0 && (
                    <span>{e.tmWatch.matchCount} similar marks, none exact</span>
                  )}
                  {e.currentStep && <span>· step: {e.currentStep}</span>}
                  {e.tmWatch && <span>· checked {timeAgo(e.tmWatch.checkedAt)}</span>}
                </div>
              </div>
              <span className="mkt-feedback-row-arrow">→</span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default TmWatch;
